import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, Trash2, Plus } from "lucide-react";
import { adminApi, errMsg } from "@/lib/proApi";

const eur = (c) => ((c || 0) / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const SHIP = { carrier: "Transporteur", mondial_relay: "Mondial Relay", pickup: "Retrait au dépôt de Chatou" };
const inp = "rounded-lg border border-[var(--oz-border)] px-3 py-2 text-sm bg-white";

const Lines = ({ products, items, setItems }) => {
  const set = (i, patch) => setItems(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  return (
    <div className="space-y-2">
      {items.map((it, i) => {
        const p = products.find((x) => x.id === it.product_id);
        return (
          <div key={i} className="flex flex-wrap gap-2 items-center" data-testid={`sim-line-${i}`}>
            <select value={it.product_id} onChange={(e) => { const np = products.find((x) => x.id === e.target.value); set(i, { product_id: np.id, pack_size: np.pack_sizes[0] }); }}
              data-testid={`sim-product-${i}`} className={`${inp} flex-1 min-w-[220px]`}>
              {products.map((x) => <option key={x.id} value={x.id}>{x.name} — {x.unit_price_ht.toFixed(2).replace(".", ",")} € HT</option>)}
            </select>
            <select value={it.pack_size} onChange={(e) => set(i, { pack_size: Number(e.target.value) })} data-testid={`sim-pack-${i}`} className={inp}>
              {p?.pack_sizes.map((s) => <option key={s} value={s}>Lot de {s}</option>)}
            </select>
            <input type="number" min="1" value={it.lots} onChange={(e) => set(i, { lots: Math.max(1, Number(e.target.value) || 1) })}
              data-testid={`sim-lots-${i}`} className={`${inp} w-20`} aria-label="nombre de lots" />
            <button onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`sim-remove-${i}`} className="p-2 text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
        );
      })}
      <button onClick={() => setItems([...items, { product_id: products[0].id, pack_size: products[0].pack_sizes[0], lots: 1 }])}
        data-testid="sim-add-line" className="inline-flex items-center gap-1 text-sm font-semibold"><Plus className="w-4 h-4" /> Ajouter un produit</button>
    </div>
  );
};

const Sheet = React.forwardRef(({ r, client, opts, testid }, ref) => (
  <div ref={ref} data-testid={testid} className="oz-print bg-white p-[10mm] shadow text-neutral-900 text-[11px]">
    <header className="flex justify-between border-b-2 border-neutral-900 pb-2">
      <div className="font-serif text-2xl">Ozentya<span className="text-amber-600">.</span></div>
      <div className="text-right"><h2 className="text-base font-bold uppercase">Simulation de devis</h2><p>Le {new Date().toLocaleDateString("fr-FR")} — document indicatif</p></div>
    </header>
    {client && <p className="mt-2"><b>Client :</b> {client}</p>}
    <table className="w-full mt-3 border-collapse" data-testid="sim-table">
      <thead><tr className="border-b border-neutral-900 text-left text-[9px] uppercase"><th className="py-1">Produit</th><th className="text-right">Quantité</th><th className="text-right">P.U. HT</th><th className="text-right">Total HT</th></tr></thead>
      <tbody>{r.lines.map((l) => (
        <tr key={`${l.product_id}-${l.pack_size}`} className="border-b border-neutral-200">
          <td className="py-1">{l.name}</td><td className="text-right">{l.lots > 1 ? `${l.lots} × ${l.pack_size} = ` : ""}{l.pieces} p.</td>
          <td className="text-right">{eur(l.unit_ht_cents)}</td><td className="text-right">{eur(l.total_ht_cents)}</td>
        </tr>))}
      </tbody>
    </table>
    <div className="mt-3 ml-auto w-72 space-y-0.5" data-testid="sim-totals">
      <p className="flex justify-between"><span>Sous-total HT</span><span>{eur(r.subtotal_ht_cents)}</span></p>
      {r.discount_ht_cents > 0 && <p className="flex justify-between text-emerald-700"><span>Remise -{r.discount_percent} %</span><span>- {eur(r.discount_ht_cents)}</span></p>}
      <p className="flex justify-between"><span>Livraison ({SHIP[opts.shipping_method]})</span><span>{eur(r.shipping_ht_cents)}</span></p>
      <p className="flex justify-between"><span>Total HT</span><span>{eur(r.total_ht_cents)}</span></p>
      <p className="flex justify-between"><span>{r.vat_mode === "dom_tom" ? "DOM-TOM : sans TVA" : `TVA ${r.vat_rate} %`}</span><span>{eur(r.vat_cents)}</span></p>
      <p data-testid="sim-total-ttc" className="flex justify-between font-bold text-sm border-t border-neutral-900 pt-1"><span>Total TTC</span><span>{eur(r.total_ttc_cents)}</span></p>
    </div>
    {!r.min_delivery_ok && opts.shipping_method !== "pickup" && <p data-testid="sim-min-warning" className="mt-2 text-red-700 font-semibold">{r.min_delivery_msg}</p>}
    <p className="mt-3 text-[9px] text-neutral-600">{r.delivery_delay} Minimum 300 € HT d'achat pour être livré, sinon retrait au {r.pickup_address}. Paiement par carte, PayPal (4X possible), Klarna ou virement. Simulation sans valeur contractuelle ; seule la facture fait foi.</p>
  </div>
));

export default function AdminQuoteSim() {
  const [products, setProducts] = useState(null);
  const [items, setItems] = useState([]);
  const [opts, setOpts] = useState({ discount_percent: 15, shipping_method: "carrier", postal_code: "", country: "FR" });
  const [client, setClient] = useState("");
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  const sheet = useRef(null);
  useEffect(() => {
    adminApi.get("/admin/pro/products").then(({ data }) => {
      const act = data.filter((p) => p.active);
      setProducts(act);
      const ex = act.find((p) => p.slug === "extreme-repouss") || act[0];
      setItems([{ product_id: ex.id, pack_size: ex.pack_sizes.includes(48) ? 48 : ex.pack_sizes[0], lots: 1 }]);
    }).catch((e) => setErr(errMsg(e)));
  }, []);
  useEffect(() => {
    if (!items.length) { setR(null); return; }
    const t = setTimeout(() => adminApi.post("/admin/pro/simulate", { items, ...opts }).then(({ data }) => { setR(data); setErr(""); }).catch((e) => setErr(errMsg(e))), 250);
    return () => clearTimeout(t);
  }, [items, opts]);
  return (
    <div data-testid="admin-quote-sim" className="max-w-5xl mx-auto px-4 sm:px-8 py-10 space-y-6">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-[var(--oz-muted)]" data-testid="sim-back"><ArrowLeft className="w-4 h-4" /> Administration</Link>
      <h1 className="font-serif text-3xl sm:text-4xl">Simulation de devis</h1>
      {err && <p data-testid="sim-error" className="text-sm text-red-600">{err}</p>}
      {products && (
        <section className="rounded-2xl bg-white border border-[var(--oz-border)] p-6 space-y-4">
          <input value={client} onChange={(e) => setClient(e.target.value)} placeholder="Nom du client (facultatif)" data-testid="sim-client" className={`${inp} w-full`} />
          <Lines products={products} items={items} setItems={setItems} />
          <div className="flex flex-wrap gap-3 items-center">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={opts.discount_percent === 15} data-testid="sim-discount"
              onChange={(e) => setOpts({ ...opts, discount_percent: e.target.checked ? 15 : 0 })} className="w-4 h-4 accent-[var(--oz-gold)]" /> Remise -15 %</label>
            <select value={opts.shipping_method} onChange={(e) => setOpts({ ...opts, shipping_method: e.target.value })} data-testid="sim-ship" className={inp}>
              {Object.entries(SHIP).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {opts.shipping_method === "carrier" && <>
              <select value={opts.country} onChange={(e) => setOpts({ ...opts, country: e.target.value })} data-testid="sim-country" className={inp}>
                {[["FR", "France / DOM-TOM"], ["BE", "Belgique"], ["DE", "Allemagne"], ["ES", "Espagne"], ["IT", "Italie"], ["LU", "Luxembourg"], ["NL", "Pays-Bas"]].map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <input value={opts.postal_code} onChange={(e) => setOpts({ ...opts, postal_code: e.target.value })} placeholder="Code postal" data-testid="sim-postal" className={`${inp} w-32`} />
            </>}
            <button onClick={() => window.print()} disabled={!r} data-testid="sim-print-btn"
              className="ml-auto inline-flex items-center gap-2 rounded-full bg-neutral-900 text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-50"><Printer className="w-4 h-4" /> Imprimer / PDF</button>
          </div>
        </section>
      )}
      {r && <Sheet r={r} client={client} opts={opts} testid="sim-sheet" />}
      {r && createPortal(<div className="oz-print-root"><Sheet ref={sheet} r={r} client={client} opts={opts} testid="sim-print-sheet" /></div>, document.body)}
    </div>
  );
}
